import { Crosshair, Film, Image as ImageIcon, MapPinOff, Play, Plus } from "lucide-react";
import { lazy, Suspense, useEffect, useMemo, useState, type ReactNode } from "react";
import { del, get, post, put, type Member, type Trip } from "../lib/api.ts";
import { isAdmin } from "../lib/rights.ts";
import { CATEGORIES_TAB, googleSegment, navigate, tripPath } from "../lib/router.tsx";
import { editorOnly } from "../shell/Shell.tsx";
import {
  describeLocation,
  inWindow,
  localTrackLine,
  positionAt,
  routeColor,
  routeLines,
  sliceUntil,
  toPointFeatures,
  tracksBetween,
  travelledLines,
  windowAt,
  type MapAsset,
  type Track,
} from "../lib/mapdata.ts";
import { SETTLE_MS } from "../lib/camera.ts";
import { angleForTime, detentsFor, TURN_MS, type Detent } from "../lib/dial.ts";
import { dayRange, dayTabLabel, daysBetween, localDayOf } from "../lib/days.ts";
import { useApi } from "../lib/useApi.ts";
import { useThumbs } from "../lib/thumbs.ts";
import { useLocalTrack } from "../lib/useLocalTrack.ts";
import { PHONE, useMediaQuery } from "../lib/useMedia.ts";
import { Card, EmptyState, ICON, NestedCard, PageState, Pill, reason } from "../ui/index.ts";
import { sectorNamed, useDangerSectors } from "../lib/danger-sectors.ts";
import {
  addCategoryNamed,
  canRemovePoi,
  editPoi,
  poiFeatures,
  removePoi,
  scoredPois,
  usePois,
  type Poi,
  type ScoredPoi,
} from "../lib/pois.ts";
import { adjustedLevel, type Mode } from "@trip/domain/danger";
import { DangerCompass } from "./DangerCompass.tsx";
import { DangerLayer, shortCredit } from "./DangerLayer.tsx";
import { ZonePanel } from "./ZonePanel.tsx";
import { MapCardSheet } from "./MapCardSheet.tsx";
import { MapCredit } from "./MapCredit.tsx";
import { MapDays, type MapDay } from "./MapDays.tsx";
import { MapFullscreen } from "./MapFullscreen.tsx";
import { MapLayers, type Layer } from "./MapLayers.tsx";
import { PoiEditor, type PoiDraft } from "./PoiEditor.tsx";
import { PoiLayer } from "./PoiLayer.tsx";
import { recentlyOpened, rememberOpened } from "../lib/recent-places.ts";

/** One empty list, so a page that is not searching hands the surface the same array twice. */
const NO_POIS: Poi[] = [];
import { PoiSearch } from "./PoiSearch.tsx";
import { PoiSheet, type PoiSnap } from "./PoiSheet.tsx";
import { PoiView } from "./PoiView.tsx";
import type { MapCard } from "./MapView.tsx";
import { TimeDial } from "./TimeDial.tsx";

// WebGL map code loads only on this page.
const MapView = lazy(() => import("./MapView.tsx").then((m) => ({ default: m.MapView })));

const WIDTHS: { label: string; ms: number | null }[] = [
  { label: "1 hour", ms: 3_600_000 },
  { label: "6 hours", ms: 6 * 3_600_000 },
  { label: "1 day", ms: 86_400_000 },
  { label: "Whole trip", ms: null },
];
/** Matches the phone's 5 m capture resolution; the whole trip is drawn as a cheaper overview. */
const TRACK_SIMPLIFY_M = 2;
const TRACK_OVERVIEW_SIMPLIFY_M = 10;
/** The slider fires on every step; routes are refetched once it settles. */
const TRACK_SETTLE_MS = 300;

/** Leaves the day strip, the recentre control and the dial clear of the markers on the first fit. */
const PHONE_FIT = { top: 150, right: 24, bottom: 200, left: 24 };
/**
 * Layer 2's one fit to the places, on a phone: clear of the chips above, and below of the switcher
 * and the sheet's best place, stacked in that order.
 */
const PHONE_POI_FIT = { top: 80, right: 24, bottom: 270, left: 24 };
/** The viewer's own zone decides where a day starts, as it does in the media library. */
const TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;
/**
 * How close two recordings may sit on the rim before they become one detent. A burst of frames
 * would otherwise make the dial feel like sandpaper. About two minutes of a 24-hour turn.
 */
const DETENT_SPACING_RAD = 0.009;
/**
 * How many recordings are drawn as cards at once. Each is a DOM node pinned to the map, so this
 * is a budget, not a preference; the rest stay as dots under the clustering layer.
 */
const MAX_CARDS = 24;
/** The dial has to stop before a thumbnail is worth fetching. */
const THUMB_SETTLE_MS = 250;
const CONFIDENCE = [
  { label: "Any location", value: "" },
  { label: "Likely (≥ 50 %)", value: "0.5" },
  { label: "Confident (≥ 80 %)", value: "0.8" },
  { label: "File GPS only", value: "1" },
];

type Histogram = {
  from: string | null;
  to: string | null;
  counts: { start: string; count: number; located: number }[];
};

/** Built once: this is read on every render, and rendering follows the dial frame by frame. */
const HOUR_FORMAT = new Intl.DateTimeFormat("en-GB", {
  hour: "numeric",
  hourCycle: "h23",
  timeZone: TIME_ZONE,
});
/** The clock hour, in the reader's own zone, that the danger colours are worked out for. */
const hourAt = (t: number) => Number(HOUR_FORMAT.format(t));
/** How often the danger map looks at the clock again. */
const CLOCK_TICK_MS = 60_000;
/** The cards the map is handed when the trip is not the layer showing. */
const NO_CARDS: MapCard[] = [];

/** Fetch every page of the map read model for the current filters (bounded). */
async function loadAll(base: string, qs: string): Promise<MapAsset[]> {
  const out: MapAsset[] = [];
  let cursor: string | null = null;
  for (let page = 0; page < 10; page++) {
    const r: { items: MapAsset[]; nextCursor: string | null } = await get(
      `${base}/map-assets?limit=1000${qs}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
    );
    out.push(...r.items);
    cursor = r.nextCursor;
    if (!cursor) break;
  }
  return out;
}

/** H. Map & Timeline: the editor drags through trip time and sees where and when footage was taken. */
export function MapPage({ trip }: { trip: Trip }) {
  const base = `/v1/trips/${trip.id}`;
  const [contributorId, setContributor] = useState("");
  const [minConfidence, setMinConfidence] = useState("");
  const [width, setWidth] = useState<number | null>(null);
  /** Keeps the map centred on the traveller as they walk; off the moment they pan away. */
  const [follow, setFollow] = useState(false);
  const [position, setPosition] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [assets, setAssets] = useState<{ items: MapAsset[] } | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<{ id: string; url: string } | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  /** The phone's playhead: one instant, not a window. Null until the trip's range is known. */
  const [playhead, setPlayhead] = useState<number | null>(null);
  /** The recording whose card was opened, if any. */
  const [openId, setOpenId] = useState<string | null>(null);
  /**
   * Somewhere the map has been asked to look: where the traveller was at the moment the dial was
   * turned to, or the moment the card now being read was taken. Turning the dial is asking
   * "where was I then", and an answer the reader has to go hunting for is not an answer.
   */
  const [lookAt, setLookAt] = useState<[number, number] | null>(null);
  /** Where the map should settle once the dial has been let go of and has been still a while. */
  const [settleAt, setSettleAt] = useState<[number, number] | null>(null);
  /**
   * Layers 2 and 3's "Centre on your position": one trip to the traveller's fix, not a follow,
   * since there the map is for looking around. A new array per tap.
   */
  const [recentreAt, setRecentreAt] = useState<[number, number] | null>(null);
  /**
   * What one turn of the dial is worth. A day to begin with, as the rim's clock face implies;
   * pinching the dial gears it finer or coarser, which is the only thing that can give a burst
   * of recordings room to be told apart -- the rim is a fixed length, so time per pixel is the
   * gear and nothing else.
   */
  const [turnMs, setTurnMs] = useState<number>(TURN_MS);
  /**
   * Somewhere to send the camera, once: a place Google found and opened in the search's sheet, or
   * the recording whose picture was tapped on the dial. A new array each time, so asking twice
   * for the same spot goes back to it.
   */
  const [goTo, setGoTo] = useState<[number, number] | null>(null);
  /** True while a finger is on the dial: nothing tidies up under a thumb that is still working. */
  const [holding, setHolding] = useState(false);
  const phone = useMediaQuery(PHONE);
  /**
   * The search field has focus. While it does, the map's furniture stands down — the chips, the
   * layer switcher and the ranked sheet — so the matches are not painted over by them.
   */
  const [searching, setSearching] = useState(false);
  const [mode, setMode] = useState<Mode>("FOOT");
  // Straight from the recorder: where the phone is, and the trail the server has not taken yet.
  const local = useLocalTrack();
  /**
   * Walking or riding. On a phone it is the native zone watcher's, the same reading the zone
   * notifications show, so the colours never say one mode while the phone buzzes the other;
   * walking until it has said anything. A desktop keeps its own buttons.
   */
  const dangerMode: Mode = phone ? (local?.zone?.mode ?? "FOOT") : mode;
  /**
   * Which of the map's layers is showing. Only a trip with the layers switched on offers a
   * choice; every other trip stays on layer 1, which is the map as it always was.
   */
  const [layer, setLayer] = useState<Layer>(1);
  const shownLayer: Layer = trip.mapLayersEnabled ? layer : 1;
  /**
   * The phone's screen cleared by a tap on the empty map, for looking at the map itself: every
   * control over it goes but the layer switch and the credit, and another tap brings them back.
   * Never carried over: a different layer, or the map opened again, is the screen as it normally is.
   */
  const [clearedOn, setClearedOn] = useState<Layer | null>(null);
  /** Layer 1 is the trip: its pins, routes, days, dial, filters and lists go when it does. */
  const tripLayer = shownLayer === 1;
  /**
   * Layer 3's zone whose panel is open, by its sector's name. Like the cleared screen, it belongs
   * to the layer it was opened on and is dropped on leaving it.
   */
  const [zoneShown, setZoneShown] = useState<string | null>(null);
  useEffect(() => {
    setClearedOn(null);
    setZoneShown(null);
  }, [shownLayer]);
  /**
   * Cleared, and on a phone: a desktop gets no new map-layer UI, so it is never cleared. Held as
   * the layer it was cleared on, so the first frame of another layer is already whole rather than
   * waiting for the effect above to catch up.
   */
  const bare = phone && clearedOn === shownLayer;

  // Nothing is fetched or drawn unless the trip has the layers switched on and the danger layer
  // is the one showing. Leaving it hands the map null, which takes the zones down.
  const danger = shownLayer === 3;
  /** Layer 2: the places and their heat. The same rule, the same way down. */
  const places = shownLayer === 2;
  /**
   * The danger map is coloured for the hour it is being read in, not for wherever the dial was
   * left: the dial is layer 1's, and a traveller checking a street now wants now's picture. Read
   * again every minute while the layer is up, so it turns over on the hour without a reload.
   */
  const [now, setNow] = useState(() => Date.now());
  // Layer 2 reads the clock too: a window closing is what makes a place urgent.
  const clocked = danger || places;
  useEffect(() => {
    if (!clocked) return;
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
    return () => clearInterval(t);
  }, [clocked]);
  const hour = hourAt(now);
  // Loaded for layer 2 as well, which scores each place by the level of the sector it sits in;
  // only layer 3 paints them.
  const { zones, sectors, attribution, period, status } = useDangerSectors(
    hour,
    dangerMode,
    clocked,
  );

  const qs =
    (contributorId ? `&contributorId=${contributorId}` : "") +
    (minConfidence ? `&minConfidence=${minConfidence}` : "");
  const histogram = useApi<Histogram>(`${base}/map-histogram?buckets=96${qs}`);
  const members = useApi<{ items: Member[] }>(`${base}/members`);

  const reload = () => {
    setLoading(true);
    loadAll(base, qs)
      .then((items) => {
        setAssets({ items });
        setError(null);
      })
      .catch((e: Error) => setError(e))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [base, qs]);

  const range = useMemo(() => {
    const times = (assets?.items ?? []).map((a) => Date.parse(a.captureAt));
    const from = histogram.data?.from ? Date.parse(histogram.data.from) : Math.min(...times);
    const to = histogram.data?.to ? Date.parse(histogram.data.to) : Math.max(...times);
    return Number.isFinite(from) && Number.isFinite(to) && to > from ? { from, to } : null;
  }, [assets, histogram.data]);
  /**
   * The dial turns whole days, not just the stretch that happens to hold footage: from 00:00:00
   * of the first day to 24:00:00 of the last. A traveller who shot nothing before noon can still
   * turn back through the morning, and a day always reads as a full turn of the rim.
   */
  const span = useMemo(() => {
    if (range === null) return null;
    const first = dayRange(localDayOf(range.from, TIME_ZONE), TIME_ZONE);
    const last = dayRange(localDayOf(range.to, TIME_ZONE), TIME_ZONE);
    return { from: Date.parse(first.from), to: Date.parse(last.to) };
  }, [range?.from, range?.to]);
  const win = range ? windowAt(range.from, range.to, position, width) : null;
  // Dragging the slider must not refetch the route on every step: useApi drops its data when the
  // path changes, so the line would blank out and the server would run a PostGIS query per pixel.
  const [trackWin, setTrackWin] = useState<{ start: number; end: number } | null>(null);
  useEffect(() => {
    // The phone slices the route client-side from a single whole-trip read, so it never narrows
    // the query: scrubbing must not touch the network.
    if (phone || !win || width === null) {
      setTrackWin(null);
      return;
    }
    const t = setTimeout(() => setTrackWin({ start: win.start, end: win.end }), TRACK_SETTLE_MS);
    return () => clearTimeout(t);
  }, [win?.start, win?.end, width, phone]);
  // Both the tolerance and the bounds come from the settled window, never from `width` directly:
  // `width` changes a render before `trackWin` does, and the fine tolerance without bounds is a
  // whole-trip read at 2 m — the most expensive query this page can ask for. Deriving both from one
  // value also means the path does not change until the window settles, so nothing is fetched twice.
  const trackQuery =
    `simplify=${trackWin ? TRACK_SIMPLIFY_M : TRACK_OVERVIEW_SIMPLIFY_M}` +
    (trackWin
      ? `&from=${new Date(trackWin.start).toISOString()}&to=${new Date(trackWin.end).toISOString()}`
      : "") +
    (contributorId ? `&userId=${contributorId}` : "");
  const tracks = useApi<{ tracks: Track[] }>(`${base}/tracks?${trackQuery}`);
  const visible = useMemo(
    () => (assets?.items ?? []).filter((a) => (win ? inWindow(a, win) : true)),
    [assets, win?.start, win?.end],
  );
  /**
   * The day being turned through: midnight to midnight around the playhead, in the reader's own
   * zone. The route, the pins and the dial's own stops are all cut to it, so everything on
   * screen is telling the same day.
   */
  const dayWindow = useMemo(() => {
    if (!phone || playhead === null || span === null) return null;
    // The dial ends at 24:00:00, which is already the next day's midnight — a day the trip never
    // saw. Turning all the way to the end means the last day it did, not an empty one after it.
    const within = Math.min(playhead, span.to - 1);
    const { from, to } = dayRange(localDayOf(within, TIME_ZONE), TIME_ZONE);
    // The end is the next midnight, which belongs to the next day; stop a millisecond short.
    return { from: Date.parse(from), to: Date.parse(to) - 1 };
  }, [
    phone,
    span?.to,
    playhead === null || span === null
      ? ""
      : localDayOf(Math.min(playhead, span.to - 1), TIME_ZONE),
  ]);

  /**
   * The phone replays a day rather than sliding a window: the whole of the day being read. The
   * day and not the trip, because the route under the pins has always been the day's — showing
   * yesterday's recordings over today's streets said the trip was somewhere it was not, and a
   * day with nothing in it is supposed to look empty.
   *
   * The whole day, including what the dial has not reached yet: the dial decides how a recording
   * is drawn, not whether it is there. Cut at the playhead, the day had no shape until you had
   * turned through all of it, and nothing on screen said what was still to come.
   */
  const shown = useMemo(
    () =>
      phone && playhead !== null
        ? (assets?.items ?? []).filter((a) => {
            const t = Date.parse(a.captureAt);
            return !dayWindow || (t >= dayWindow.from && t <= dayWindow.to);
          })
        : visible,
    [phone, assets, visible, dayWindow?.from, dayWindow?.to],
  );
  // Land on the last recording, so the map opens on the finished trip and turning back replays
  // it — but the dial may then be turned anywhere in the day, which is why the clamp is the
  // day's span and the landing is the footage's end.
  useEffect(() => {
    if (!phone || range === null || span === null) return;
    setPlayhead((p) => (p === null ? range.to : Math.min(span.to, Math.max(span.from, p))));
  }, [phone, range?.to, span]);

  const points = useMemo(() => toPointFeatures(shown), [shown]);

  /**
   * The route the map draws: on the phone, only the day the dial is parked in.
   *
   * A day's map should show a day's walking. Carrying every other day across it says the
   * traveller was in several places at once, and the days still to come give away where they
   * are going to be — on a long trip the screen ends up a knot of line with no way to tell
   * which part of it is today.
   *
   * Clipped here rather than asked for: the whole trip is already in hand from one read, and
   * narrowing the query instead would put a PostGIS round trip behind every turn of the dial.
   * Keyed on the day, so turning within one does not rebuild the geometry.
   */
  const allTracks = tracks.data?.tracks ?? [];
  const dayTracks = useMemo(
    () => (dayWindow ? tracksBetween(allTracks, dayWindow.from, dayWindow.to) : allTracks),
    [tracks.data, dayWindow?.from, dayWindow?.to],
  );
  const routes = useMemo(() => routeLines(dayTracks), [dayTracks]);
  const localPath = useMemo(() => localTrackLine(local?.pending ?? []), [local?.pending]);

  // --- layer 2 -------------------------------------------------------------
  const poiList = usePois(trip.id, places);
  /** The category the heat is narrowed to; null is all of them. */
  const [category, setCategory] = useState<string | null>(null);
  // A category deleted since it was picked would leave the heat and the list empty with no chip
  // pressed to say why: back to everything instead.
  useEffect(() => {
    if (
      category !== null &&
      poiList.categories &&
      !poiList.categories.some((c) => c.id === category)
    )
      setCategory(null);
  }, [category, poiList.categories]);
  /**
   * The place open in the place view, from a tapped pin or a row of the sheet. Kept while the
   * editor is open on it, so closing the editor is the view again.
   */
  const [viewing, setViewing] = useState<string | null>(null);
  /**
   * Where there is no place view (a desktop, which gets no new map-layer UI): the place whose pin
   * was tapped, for the sheet to open on; a new object per tap.
   */
  const [poiPick, setPoiPick] = useState<{ id: string } | null>(null);
  /**
   * The place open in the editor: one the trip has, a new one about to be put at the middle of the
   * map, or one Google found, which brings its own position and hours. Layer 2's alone: leaving the
   * layer drops it, so coming back is the sheet, not a form left half typed.
   */
  const [editing, setEditing] = useState<{ id: string } | "new" | null>(null);
  /**
   * The phone's sheet: the card under the layer switch, or the full list over the map and the
   * switch. Kept here, not in the sheet, because the switch and the + are laid out around it; and
   * kept while a place opened from the full list is viewed, so closing the view is the list again.
   */
  const [poiSnap, setPoiSnap] = useState<PoiSnap>("card");
  useEffect(() => {
    if (!places) {
      setViewing(null);
      setPoiPick(null);
      setEditing(null);
      setPoiSnap("card");
    }
  }, [places]);
  // The last place gone takes the sheet with it; the next one to arrive brings back the card,
  // not a full list pulled up over the map with one row in it.
  const noPlaces = poiList.pois?.length === 0;
  useEffect(() => {
    if (noPlaces) setPoiSnap("card");
  }, [noPlaces]);
  /**
   * The middle of the map, asked for while layer 2 is up: where closeness is measured from when
   * the phone has no fix, and where a new place goes.
   */
  const [mapCentre, setMapCentre] = useState<[number, number] | null>(null);
  /**
   * Where closeness is measured from: where the traveller is standing, and failing that the
   * middle of the map, so that panning to a neighbourhood asks what is worth walking to there.
   * Keyed on the numbers, so a fix that has not moved does not rebuild the heat.
   */
  const hereLat = local?.here?.lat;
  const hereLon = local?.here?.lon;
  const fixed = hereLat !== undefined && hereLon !== undefined;
  // With a fix the middle of the map plays no part, so panning does not re-score anything.
  const centreLon = fixed ? undefined : mapCentre?.[0];
  const centreLat = fixed ? undefined : mapCentre?.[1];
  const from = useMemo(
    () =>
      hereLat !== undefined && hereLon !== undefined
        ? { lat: hereLat, lon: hereLon }
        : centreLat !== undefined && centreLon !== undefined
          ? { lat: centreLat, lon: centreLon }
          : null,
    [hereLat, hereLon, centreLat, centreLon],
  );
  /**
   * Every place still to do, scored. Held back until the danger snapshot has arrived or failed
   * to: scored without it, every pin would say "no safety data" for the moment it takes to load,
   * and then the heat would be rebuilt. Unavailable, it scores with no sectors, and every pin
   * says so, which is then the truth.
   */
  const scored = useMemo(
    () =>
      poiList.pois && status !== "loading"
        ? scoredPois(poiList.pois, now, from, sectors ?? [], dangerMode)
        : null,
    [poiList.pois, status, now, from, sectors, dangerMode],
  );
  /** What the heat and the pins draw: one collection, rebuilt only when something in it moves. */
  const poiDrawn = useMemo(
    () => (places && scored ? poiFeatures(scored, category, poiList.categories ?? []) : null),
    [places, scored, category, poiList.categories],
  );
  /** The sheet's list: the same places as the heat, in the same order, narrowed the same way. */
  const ranked = useMemo(
    () => (scored && category !== null ? scored.filter((p) => p.categoryId === category) : scored),
    [scored, category],
  );
  /** What has been visited, from the list itself, since the scored one leaves it out. */
  const doneList = useMemo(
    () =>
      (poiList.pois ?? []).filter(
        (p) => p.ticked !== null && (category === null || p.categoryId === category),
      ),
    [poiList.pois, category],
  );
  /**
   * A tick or a vote: sent, and the place as the server then has it put into the list, which
   * re-scores the heat and the sheet together. No guess goes in first, and nothing is queued: a
   * write that does not arrive throws, and the sheet says so on the row.
   */
  const poiWrite = (what: "tick" | "vote") => async (id: string, on: boolean) => {
    const path = `${base}/pois/${id}/${what}`;
    poiList.replace(on ? await put<Poi>(path, undefined) : await del<Poi>(path));
  };
  /** The place in the view, as the list now has it, so a write shows the server's answer. */
  const viewedPoi = viewing === null ? null : (poiList.pois?.find((p) => p.id === viewing) ?? null);
  /**
   * The viewed place, scored as the sheet scores it: how far, how safe, how urgent. A ticked place
   * is out of the scored list, so it is scored on its own here, its tick kept.
   */
  const viewedScored: Poi | ScoredPoi | null = useMemo(() => {
    if (viewedPoi === null) return null;
    const ranked = scored?.find((p) => p.id === viewedPoi.id);
    if (ranked) return ranked;
    if (scored === null) return viewedPoi;
    const alone = scoredPois(
      [{ ...viewedPoi, ticked: null }],
      now,
      from,
      sectors ?? [],
      dangerMode,
    )[0];
    return alone ? { ...alone, ticked: viewedPoi.ticked } : viewedPoi;
  }, [viewedPoi, scored, now, from, sectors, dangerMode]);
  // Opening a place turns the map to it, and closing the view lets the ask go, so the same place
  // opened again after panning away is a new ask rather than the old one standing. Keyed on the
  // position, not the place, so the ask lands once the list has it too.
  const viewedLon = viewedPoi?.lon;
  const viewedLat = viewedPoi?.lat;
  useEffect(() => {
    if (viewedLon === undefined || viewedLat === undefined) {
      if (viewing === null) setLookAt(null);
      return;
    }
    setLookAt([viewedLon, viewedLat]);
  }, [viewing, viewedLon, viewedLat]);
  // A place deleted while in view (by somebody else, or a sync) closes it, and is let go of.
  const viewedGone = viewing !== null && poiList.pois !== null && !viewedPoi;
  useEffect(() => {
    if (viewedGone) setViewing(null);
  }, [viewedGone]);
  /** The id in the editor, where the editor is open on a place the trip already has. */
  const editingId = editing !== null && editing !== "new" ? editing.id : null;
  /** The place in the editor, as the list now has it, so a vote shows the server's count. */
  const editedPoi =
    editingId !== null ? (poiList.pois?.find((p) => p.id === editingId) ?? null) : null;
  const poiEditorOpen = editing === "new" || editedPoi !== null;
  // A place deleted while open in the editor closes it; let go of it too, or the editor would
  // spring open on it again if a later fetch brought it back.
  const editedGone = editingId !== null && poiList.pois !== null && !editedPoi;
  useEffect(() => {
    if (editedGone) setEditing(null);
  }, [editedGone]);
  /**
   * Into the editor. Editing a place comes from its view, which is back when the editor closes;
   * adding one leaves any view behind, so closing the form is the sheet.
   */
  const openEditor = (what: { id: string } | "new") => {
    if (what === "new" || "place" in what) setViewing(null);
    setPoiPick(null);
    setEditing(what);
  };
  /** The pencil at the end of the chips: the Places page, on its Categories tab. */
  const openCategories = () => navigate(tripPath(trip.id, "places", CATEGORIES_TAB));
  /**
   * The editor's save: the place sent, and the server's answer put into the list. A new place goes
   * where the middle of the map is at the moment of saving, so it can be panned into place under
   * the cross while the form is open; one Google found goes where Google has it, which is the
   * whole point of having looked it up. Either way it is the one create path, which already takes
   * windows. A name is sent only when it changed, so an imported place's name is never rewritten
   * by an edit of its hours.
   */
  const poiSave = async (draft: PoiDraft) => {
    if (editedPoi) {
      poiList.replace(await editPoi(trip.id, editedPoi, draft));
    } else if (editing === "new") {
      const at = mapCentre ? { lat: mapCentre[1], lon: mapCentre[0] } : null;
      if (!at) throw new Error("The middle of the map is not known yet.");
      poiList.replace(
        await post<Poi>(`${base}/pois`, {
          name: draft.name,
          lat: at.lat,
          lon: at.lon,
          categoryId: draft.categoryId,
          ...(draft.note !== null ? { note: draft.note } : {}),
          windows: draft.windows,
        }),
      );
    }
    setEditing(null);
  };
  /**
   * A category for the trip, from the place editor, where only its name is asked: it gets the plain
   * pin and the first colour no category wears yet, both changed in the category editor. The
   * server answers with the one it already had under that name, which keeps its own face.
   */
  const poiCategory = async (name: string) => {
    const c = await addCategoryNamed(trip.id, name, poiList.categories ?? []);
    poiList.addCategory(c);
    return c;
  };
  /** Deleting a place from its editor: once the server has, it leaves the list and both close. */
  const poiRemove = async (id: string) => {
    await removePoi(trip.id, id);
    poiList.remove(id);
    setEditing(null);
    setViewing(null);
  };
  const myInitial = (members.data?.items.find((m) => m.isYou)?.displayName ?? "")
    .trim()[0]
    ?.toUpperCase();
  const name = (id: string) =>
    members.data?.items.find((m) => m.userId === id)?.displayName ?? "Someone";
  /** The reader, for the place view: whose own like, visit and place it is. */
  const me = members.data?.items.find((m) => m.isYou)?.userId ?? null;
  /** A member who is not on the list once it has loaded has left, as the API names them. */
  const memberName = (id: string) =>
    members.data
      ? (members.data.items.find((m) => m.userId === id)?.displayName ?? "Former member")
      : "Someone";
  /** A member's colour for their face on a place, or null once they have left. */
  const colorOf = (id: string) => members.data?.items.find((m) => m.userId === id)?.color ?? null;
  /**
   * On a phone, a place being edited (from its view) gets the panel the Places page gives it: the
   * scrim and the 90vh sheet, not the half-height panel the map lays a form in. A new place keeps
   * the map in sight, since it is panned into place under the cross; a desktop is as it was.
   */
  const wrapEditor = (form: ReactNode) =>
    phone && editedPoi !== null ? <div className="sheet-scrim places-poi-panel">{form}</div> : form;
  /** The place view is up: over the map, in the sheet's place, and under neither editor. */
  // Searching takes it down with the rest of the furniture: it stands over the map, so it would
  // stand over the matches. Closing the search brings it back as it was.
  const viewOpen = phone && places && viewedScored !== null && !poiEditorOpen && !searching;
  const current = shown.find((a) => a.assetId === selected) ?? null;
  const unlocated = shown.filter((a) => !a.location).length;
  const fmt = (t: number) =>
    new Date(t).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  /**
   * Where the map should look for a given moment: where the traveller was, if their route was
   * recorded, and otherwise the last place they are known to have taken something. A trip with
   * location sharing off has no line to follow, and "nothing happens" is not an answer to a dial
   * somebody is turning.
   */
  function lookFor(t: number): [number, number] | null {
    const mine = dayTracks.find((d) => d.points.length > 0);
    const was = mine ? positionAt(mine.points, t) : null;
    if (was) return was;
    const placed = (assets?.items ?? []).filter((a) => a.location && Date.parse(a.captureAt) <= t);
    const last = placed[placed.length - 1];
    return last?.location ? [last.location.lon, last.location.lat] : null;
  }

  async function play(a: MapAsset) {
    setPreviewError(null);
    try {
      const detail = await get<{ components: { key: string }[] }>(`${base}/assets/${a.assetId}`);
      const key = detail.components[0]?.key ?? "";
      const g = await post<{ url: string }>(`${base}/media-grants`, {
        ...(a.kind === "VIDEO" ? { variant: "PREVIEW" } : {}),
        reference: { kind: "ASSET_COMPONENT", assetId: a.assetId, componentKey: key },
      });
      setPreview({ id: a.assetId, url: g.url });
    } catch (e) {
      setPreviewError(reason(e as Error));
    }
  }

  const max = Math.max(1, ...(histogram.data?.counts.map((c) => c.count) ?? [1]));
  const windowLabel = win ? `${fmt(win.start)} – ${fmt(win.end)}` : "No dated footage yet";
  const countLabel = `${shown.length} ${shown.length === 1 ? "recording" : "recordings"}`;

  // --- the dial ------------------------------------------------------------
  /**
   * The tidy-up. While the dial is being turned the map only keeps the needle inside its dead
   * zone — the least movement that keeps it on screen. Two seconds after the finger lifts and
   * the dial stops moving, the needle is put in the middle properly. Any new turn cancels it,
   * so this never happens under somebody's thumb.
   */
  useEffect(() => {
    if (!phone || holding || playhead === null) return;
    const t = setTimeout(() => setSettleAt(lookFor(playhead)), SETTLE_MS);
    return () => clearTimeout(t);
    // `lookFor` reads the day's tracks and recordings, which move with the playhead anyway.
  }, [phone, holding, playhead, dayTracks, assets]);

  /** Every moment the trip holds: the rim's marks, and what an arrow key steps between. */
  const detents: Detent[] = useMemo(
    () =>
      span === null
        ? []
        : detentsFor(
            (assets?.items ?? []).map((a) => Date.parse(a.captureAt)).filter(Number.isFinite),
            span.from,
            DETENT_SPACING_RAD,
            turnMs,
          ),
    [assets, span, turnMs],
  );
  /**
   * The ones letting go may settle on: this day's, and no others. Snapping to the nearest moment
   * in the whole trip meant that releasing the dial on a day with no footage dragged the map
   * back to yesterday's last photograph. A day with nothing in it has nothing to settle on, and
   * the dial stays where it was put — an arrow key still steps across the boundary deliberately.
   */
  const settleOn: Detent[] = useMemo(
    () =>
      dayWindow === null
        ? detents
        : detents.filter((d) => d.t >= dayWindow.from && d.t <= dayWindow.to),
    [detents, dayWindow?.from, dayWindow?.to],
  );
  // Keyed on the slice indices, not on the playhead: the dial moves every frame, the geometry
  // only when a point is actually crossed.
  const keptKey =
    playhead === null ? "" : dayTracks.map((t) => sliceUntil(t.points, playhead)).join(",");
  // Deliberately keyed on `keptKey` rather than on `playhead`: the dial moves every frame and
  // this must not rebuild geometry until a point is actually crossed.
  const travelled = useMemo(
    () => (playhead === null ? null : travelledLines(dayTracks, playhead)),
    [keptKey, dayTracks],
  );
  // Null on a day this person did not walk, rather than their nearest fix from another one:
  // a marker standing somewhere they were not is worse than no marker.
  const head = useMemo(() => {
    if (playhead === null) return null;
    const mine = dayTracks.find((t) => t.points.length > 0);
    return mine ? positionAt(mine.points, playhead) : null;
  }, [keptKey, dayTracks, playhead]);

  /**
   * The recording the needle is on: the newest one at or before the playhead. Its thumbnail is
   * asked for on its own — one grant, not a screenful — and only when the server says one exists.
   */
  const atPlayhead = useMemo(() => {
    if (playhead === null) return null;
    const dated = shown.filter((a) => Number.isFinite(Date.parse(a.captureAt)));
    // Nearest, not the newest behind: the dial lands between recordings as often as on one, and
    // a needle that waits for the next to be passed says nothing about the one just ahead.
    return dated.reduce<(typeof dated)[number] | null>(
      (best, a) =>
        best === null ||
        Math.abs(Date.parse(a.captureAt) - playhead) <
          Math.abs(Date.parse(best.captureAt) - playhead)
          ? a
          : best,
      null,
    );
  }, [shown, playhead]);
  /**
   * The recordings drawn as cards: the newest that have a place, newest first, capped. Scrubbing
   * forward reveals them where they were taken, which is the whole point of the playhead.
   */
  /**
   * The recording in focus on the map: the nearest one the map can actually draw. The needle's
   * own `atPlayhead` is the nearest of all, placed or not -- a recording with no position is
   * still a moment the dial stops on, and the readout says so -- but a pin has to be somewhere.
   */
  const focused = useMemo(() => {
    if (playhead === null) return null;
    return shown
      .filter((a) => a.location && Number.isFinite(Date.parse(a.captureAt)))
      .reduce<(typeof shown)[number] | null>(
        (best, a) =>
          best === null ||
          Math.abs(Date.parse(a.captureAt) - playhead) <
            Math.abs(Date.parse(best.captureAt) - playhead)
            ? a
            : best,
        null,
      );
  }, [shown, playhead]);

  const carded = useMemo(() => {
    if (!phone) return [];
    const passed =
      playhead === null ? shown : shown.filter((a) => Date.parse(a.captureAt) <= playhead);
    const budget = passed
      .filter((a) => a.location)
      .slice(-MAX_CARDS)
      .reverse();
    // The one in focus always carries its picture, even when it is still ahead of the dial and
    // even when the budget is full: it is the recording the reader is being pointed at.
    return focused && !budget.some((a) => a.assetId === focused.assetId)
      ? [focused, ...budget]
      : budget;
  }, [phone, shown, playhead, focused]);
  const thumbIds = useMemo(() => {
    const ids = carded.filter((a) => a.thumbReady !== false).map((a) => a.assetId);
    if (atPlayhead?.thumbReady && !ids.includes(atPlayhead.assetId)) ids.push(atPlayhead.assetId);
    return ids;
  }, [carded, atPlayhead]);
  // The cards appear the instant the dial passes them, but their thumbnails are asked for only
  // once it stops: a fast scrub crosses dozens of recordings, and each new set would otherwise
  // be a grant request nobody waited to see.
  const [settledIds, setSettledIds] = useState<string[]>([]);
  const idKey = thumbIds.join(",");
  useEffect(() => {
    const t = setTimeout(
      () => setSettledIds(idKey === "" ? [] : idKey.split(",")),
      THUMB_SETTLE_MS,
    );
    return () => clearTimeout(t);
  }, [idKey]);
  const thumbs = useThumbs(trip.id, settledIds);
  const dialThumb =
    atPlayhead && thumbs[atPlayhead.assetId]
      ? {
          url: thumbs[atPlayhead.assetId]!,
          alt: `${atPlayhead.kind === "VIDEO" ? "Video" : "Photo"} by ${name(atPlayhead.contributorId)}`,
        }
      : null;

  /**
   * The days the dial covers, with where each one should land: the first recording of that day,
   * so picking a day puts the traveller at something that happened rather than at an empty
   * midnight; a day with nothing in it lands at its own start.
   */
  const days: MapDay[] = useMemo(() => {
    if (span === null) return [];
    const times = (assets?.items ?? [])
      .map((a) => Date.parse(a.captureAt))
      .filter(Number.isFinite)
      .sort((a, b) => a - b);
    // `span.to` is 24:00:00 — the exclusive end, which is already the next day's midnight. The
    // last day the dial actually covers is the millisecond before it.
    return daysBetween(span.from, span.to - 1, TIME_ZONE).map((day) => {
      const { from, to } = dayRange(day, TIME_ZONE);
      const start = Date.parse(from);
      const end = Date.parse(to);
      const mine = times.filter((t) => t >= start && t < end);
      return {
        day,
        at: Math.min(span.to, Math.max(span.from, mine[0] ?? start)),
        count: mine.length,
      };
    });
  }, [assets, span]);
  const currentDay = playhead === null ? "" : localDayOf(playhead, TIME_ZONE);

  /**
   * Midnight, as an angle, for every day after the first — where the dial is made to resist.
   *
   * Read from each day's own start rather than assumed to be a whole turn apart: the dial turns
   * at one turn per 24 hours, but the day a clock changes is 23 or 25 hours long, so its
   * midnight does not land on a multiple of a turn.
   */
  const dayBoundaries = useMemo(() => {
    if (span === null) return [];
    return daysBetween(span.from, span.to - 1, TIME_ZONE)
      .slice(1)
      .map((day) => angleForTime(Date.parse(dayRange(day, TIME_ZONE).from), span.from, turnMs));
  }, [span, turnMs]);

  const cards: MapCard[] = useMemo(
    () =>
      carded.map((a) => ({
        assetId: a.assetId,
        lon: a.location!.lon,
        lat: a.location!.lat,
        url: thumbs[a.assetId] ?? null,
        kind: a.kind,
        estimated: a.location!.source !== "METADATA",
        locationLabel: describeLocation(a),
        focus: a.assetId === focused?.assetId,
        label: `${a.kind === "VIDEO" ? "Video" : "Photo"} by ${name(a.contributorId)}, ${fmt(
          Date.parse(a.captureAt),
        )}`,
      })),
    // `name` and `fmt` only read data that is already loaded.
    [carded, thumbs, members.data, focused],
  );

  /** The rim says the time of day; this says which day, and what is under the needle. */
  const readout = useMemo(() => {
    if (playhead === null) return windowLabel;
    const when = new Date(playhead);
    // The same label the day strip uses, so the two agree and the line stays short enough to
    // keep the count on screen.
    const day = dayTabLabel(localDayOf(playhead, TIME_ZONE));
    const time = when.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    // The map cannot draw a recording with no place, so the readout says so rather than leaving
    // the count looking wrong against the markers.
    const noPlace = unlocated > 0 ? ` · ${unlocated} no place` : "";
    return `${day} · ${time} · ${countLabel}${noPlace}`;
  }, [playhead, countLabel, windowLabel, unlocated]);

  const filters = (
    <form className="row wrap" aria-label="Map filters" onSubmit={(e) => e.preventDefault()}>
      <label>
        <span className="label">Person</span>
        <select value={contributorId} onChange={(e) => setContributor(e.target.value)}>
          <option value="">Everyone</option>
          {members.data?.items.map((m) => (
            <option key={m.userId} value={m.userId}>
              {m.isYou ? "You" : m.displayName}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span className="label">Location</span>
        <select value={minConfidence} onChange={(e) => setMinConfidence(e.target.value)}>
          {CONFIDENCE.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span className="label">Window</span>
        <select
          value={String(width)}
          onChange={(e) => setWidth(e.target.value === "null" ? null : Number(e.target.value))}
        >
          {WIDTHS.map((w) => (
            <option key={w.label} value={String(w.ms)}>
              {w.label}
            </option>
          ))}
        </select>
      </label>
    </form>
  );

  const timeline = (
    <Card className="timeline">
      <div className="histogram" aria-hidden="true">
        {histogram.data?.counts.map((c) => (
          <span
            key={c.start}
            style={{ height: `${Math.round((c.count / max) * 100)}%` }}
            className={
              win && Date.parse(c.start) >= win.start && Date.parse(c.start) < win.end ? "in" : ""
            }
          />
        ))}
      </div>
      <label className="field">
        <span className="sr">Position in trip time</span>
        <input
          type="range"
          min={0}
          max={1000}
          step={1}
          value={Math.round(position * 1000)}
          disabled={width === null || !range}
          onChange={(e) => setPosition(Number(e.target.value) / 1000)}
          aria-valuetext={win ? `${fmt(win.start)} to ${fmt(win.end)}` : "Whole trip"}
        />
      </label>
      <p className="muted small" style={{ margin: 0 }} aria-live="polite">
        {windowLabel} · {countLabel}
        {unlocated > 0 ? ` · ${unlocated} without a location` : ""}
      </p>
    </Card>
  );

  /** The ranked places, along the bottom: the card, or pulled up the full list. */
  const sheetShown =
    places &&
    !poiEditorOpen &&
    !viewOpen &&
    !searching &&
    !!ranked &&
    (poiList.pois?.length ?? 0) > 0;
  const poiSheet = sheetShown && ranked && (
    <PoiSheet
      pois={ranked}
      done={doneList}
      now={now}
      onTick={poiWrite("tick")}
      onVote={poiWrite("vote")}
      // The phone's place view; a desktop keeps the row opening the editor.
      onOpen={phone ? setViewing : undefined}
      onEdit={(id) => openEditor({ id })}
      selected={phone ? null : poiPick}
      fromCentre={!fixed}
      // The phone lays the switch and the + out around it; a desktop leaves it to itself.
      snap={phone ? poiSnap : undefined}
      onSnap={phone ? setPoiSnap : undefined}
      // The phone's rows start with their category's emoji; a desktop's stay as they were.
      categories={phone ? (poiList.categories ?? undefined) : undefined}
      me={me}
      colorOf={colorOf}
    />
  );
  /** The phone's layer 2 lays its bottom out as one column: see the dock below. */
  const dock = phone && places && trip.mapLayersEnabled;
  /** The list pulled up over the whole map, the switch and the + with it. */
  const listFull = dock && sheetShown && poiSnap === "full" && !bare;
  const addShown = places && !poiEditorOpen && !viewOpen && poiList.categories !== null && !bare;
  /**
   * A tap on the empty map clears the screen, or brings it back; the phone's alone. Not while a
   * form, a place or a recording is open over the map: there a tap is panning under the form or
   * glancing past the card, and clearing would leave it floating on a bare map.
   */
  /** The zone whose panel is open, as the snapshot has it; null when none is or it has gone. */
  const zoneSector =
    danger && phone && zoneShown !== null && sectors ? sectorNamed(sectors, zoneShown) : null;
  const emptyTap =
    phone && !poiEditorOpen && !viewOpen && openId === null
      ? () =>
          // With a zone's panel open, the tap closes the panel and nothing more; the next one
          // clears the screen. (A place's view differs: while it is open, empty taps do nothing.)
          zoneSector
            ? setZoneShown(null)
            : setClearedOn((on) => (on === shownLayer ? null : shownLayer))
      : undefined;
  /**
   * A tap on a zone, on a phone. Zones cover the whole city, so there a tap almost never lands on
   * empty map: a tap on the zone whose panel is open closes it and clears the screen, and while
   * cleared a tap on any zone brings the screen back rather than opening a panel.
   */
  const zoneTap = (sector: string): boolean => {
    if (bare) setClearedOn(null);
    else if (zoneSector?.sector === sector) {
      setZoneShown(null);
      setClearedOn(shownLayer);
    } else {
      setZoneShown(sector);
      return true;
    }
    return false;
  };
  /**
   * Where the traveller stands, for the compass. The sector and the mode are the zone watcher's;
   * the level is worked out here, at the page's hour, from the same snapshot that paints the map,
   * so the pointer, the colour under it and the panel it opens never disagree. The watcher's own
   * level is for the notifications and is a fix old: a still traveller keeps it across the hour.
   * A sector the snapshot does not know, or no level from the watcher, is no data, never a guess.
   */
  const watched = local?.zone ?? null;
  const standing =
    watched && watched.sector !== null && watched.level !== null && sectors
      ? sectorNamed(sectors, watched.sector)
      : null;
  const compassZone = watched && {
    mode: watched.mode,
    sector: standing?.sector ?? null,
    level: standing
      ? adjustedLevel(
          watched.mode === "FOOT" ? standing.foot.score : standing.vehicle.score,
          hour,
          watched.mode,
        )
      : null,
  };

  /**
   * The dial is on screen: the single answer for the dial itself and for whatever sits above it.
   * A trip with no dated footage has no span to turn, so draws no dial and reserves no room.
   */
  const dialShown = tripLayer && !bare && span !== null && playhead !== null;
  /** The day tabs are up, along the top of the phone's map. */
  const daysShown = tripLayer && !bare && days.length > 1 && playhead !== null;
  /** The category chips are up, along the top of the map. Nothing to narrow is nothing to show. */
  const chipsShown =
    places && poiList.categories !== null && (poiList.pois?.length ?? 0) > 0 && !bare;
  /**
   * The search bar is up, along the very top. Not tied to the chips: the bar also finds places the
   * trip does not have yet, which is exactly what a trip with none of its own needs, so it stands
   * as soon as there is a list to search — empty or not. The chips then are not there, and what
   * sits under the bar (the credit, the matches) must drop their height; see the stylesheet.
   */
  /** Read only while searching: the surface is the only thing that asks for it. */
  const recent = searching && poiList.pois ? recentlyOpened(trip.id, poiList.pois) : NO_POIS;
  const searchShown = places && poiList.categories !== null && poiList.pois !== null && !bare;
  /**
   * The phone's credit: closed on every new layer, under whatever stands at the top of this one.
   * Safety holds it in the column with the compass, whose height is not fixed.
   */
  const credit = phone && !searching && (
    <MapCredit
      key={shownLayer}
      below={daysShown ? "days" : chipsShown ? "chips" : searchShown ? "search" : undefined}
      crime={danger && attribution ? shortCredit(attribution) : null}
    />
  );

  const mapBox = (
    <div className="map-box" data-cleared={bare ? "true" : undefined}>
      <Suspense fallback={<div className="map skeleton" />}>
        <MapView
          points={points}
          routes={travelled ?? routes}
          selectedId={selected}
          onSelect={setSelected}
          fitPadding={phone ? PHONE_FIT : 48}
          poiFitPadding={phone ? PHONE_POI_FIT : 48}
          ghost={travelled ? routes : null}
          // Not on layer 2: the traveller's own needle outranks an ask to look elsewhere, so with
          // a track under the dial it would keep the map from turning to a place that was opened.
          head={places ? null : head}
          cards={tripLayer ? cards : NO_CARDS}
          onOpen={setOpenId}
          // On every layer: not a pin but where the traveller stands, which is what layer 2's
          // "near me" is measured from and what layer 3's zones are read against.
          here={local?.here ?? null}
          localPath={localPath}
          initial={myInitial ?? ""}
          follow={tripLayer && follow}
          centre={lookAt}
          settle={settleAt}
          recentre={recentreAt}
          look={goTo}
          // Layer 1's dial, so a recording's pin is drawn against where the dial stands.
          dialAt={phone ? playhead : null}
          zoomButtons={!phone}
          // Loaded for layer 2's scores as well, but painted on layer 3 alone.
          zones={danger ? zones : null}
          // The phone's alone: a desktop keeps its legend and no new map-layer UI.
          selectedZone={zoneSector?.sector ?? null}
          onZone={danger && phone ? zoneTap : undefined}
          showTrip={tripLayer}
          pois={poiDrawn}
          poiCategories={places ? poiList.categories : null}
          onCentre={places ? setMapCentre : undefined}
          // Not while the editor is up: a tap on a pin under the form is a slip, not a pick.
          onPoi={
            places && !poiEditorOpen
              ? phone
                ? setViewing
                : (id: string) => setPoiPick({ id })
              : undefined
          }
          onEmptyTap={emptyTap}
          ownCredit={phone}
        />
      </Suspense>
      {/* Switch off: nothing, not even a word. Switch on and no data: one honest line. */}
      {/* The phone's legend: where the traveller stands, on the strip of levels. Not while the
          screen is cleared, and not without data, where the line that takes its place says why. */}
      {danger && phone && (
        // One column down the top of the map: the "i" stands under the compass however many
        // lines the compass takes, so it can never be covered.
        <div className="danger-top">
          {status === "ready" && !bare && (
            <DangerCompass zone={compassZone} hour={hour} onOpen={setZoneShown} />
          )}
          {status === "unavailable" && (
            <DangerLayer mode={dangerMode} hour={hour} attribution={null} />
          )}
          {credit}
        </div>
      )}
      {!danger && credit}
      {danger && !phone && status !== "loading" && (
        <DangerLayer
          mode={mode}
          hour={hour}
          attribution={status === "ready" ? attribution : null}
          onMode={setMode}
        />
      )}
      {zoneSector && attribution && (
        <ZonePanel
          key={zoneSector.sector}
          sector={zoneSector}
          mode={dangerMode}
          hour={hour}
          attribution={attribution}
          period={period}
          onClose={() => setZoneShown(null)}
        />
      )}
      {searchShown && poiList.categories && poiList.pois && (
        <PoiSearch
          tripId={trip.id}
          pois={poiList.pois}
          // The phone's rows start with their category's emoji; a desktop's stay as they were.
          categories={phone ? poiList.categories : undefined}
          chips={chipsShown}
          full={phone}
          recent={recent}
          // Where the member is looking, so Google's answers lean there. Both numbers or neither.
          centre={mapCentre}
          // A pin is not tappable under the editor, so a result is not either.
          disabled={poiEditorOpen}
          // A desktop has room for the card and keeps its furniture; only the phone takes a mode.
          onSearching={phone ? setSearching : undefined}
          onOpen={(id) => {
            rememberOpened(trip.id, id);
            if (phone) setViewing(id);
            else setPoiPick({ id });
          }}
          onAdd={(hit) => navigate(tripPath(trip.id, "places", googleSegment(hit.placeId)))}
          // A new array each time, so opening the same result again goes back to it.
          onLook={(hit) => setGoTo([hit.lon, hit.lat])}
          me={me}
          colorOf={colorOf}
        />
      )}
      {chipsShown && !searching && poiList.categories && (
        <PoiLayer
          categories={poiList.categories}
          active={category}
          onCategory={setCategory}
          // The pencil is phone-only, as the map's other phone controls are. Its destination, the
          // Places page's Categories tab, works on any width, so a desktop pencil is an opt-in the
          // owner can ask for; it was tried on this branch and reverted without that approval.
          // The old reasoning for the gate was that the pencil used to open a sheet, which is phone
          // UI; the destination is navigation, which is not, so the gate is a convention, not a need.
          onEditCategories={phone ? openCategories : undefined}
        />
      )}
      {/* Could not be had at all, and nothing saved from last time: an error, not an empty map. */}
      {places && poiList.pois === null && poiList.error && (
        <p className="map-layer-empty" role="alert">
          {poiList.error}
        </p>
      )}
      {/* A list, but qualified: the saved one, or one that could not be saved for next time. */}
      {places && poiList.pois !== null && poiList.error && (
        <p className="poi-notice" role="status" aria-label="Places">
          {poiList.error}
        </p>
      )}
      {/* Nothing to place yet. Offered and empty rather than hidden, so the switcher does not
          change shape the day there is. */}
      {places && poiList.pois?.length === 0 && (
        <p className="map-layer-empty" role="status">
          No places yet
        </p>
      )}
      {places &&
        (poiList.pois?.length ?? 0) > 0 &&
        poiList.pois!.every((p) => p.ticked !== null) && (
          <p className="map-layer-empty" role="status">
            Every place has been visited
          </p>
        )}
      {!dock && poiSheet}
      {viewOpen && viewedScored && (
        <PoiView
          key={viewedScored.id}
          tripId={trip.id}
          poi={viewedScored}
          category={poiList.categories?.find((c) => c.id === viewedScored.categoryId) ?? null}
          me={me}
          now={now}
          fromCentre={!fixed}
          nameOf={memberName}
          colorOf={colorOf}
          onClose={() => setViewing(null)}
          onVote={(on) => poiWrite("vote")(viewedScored.id, on)}
          onTick={(on) => poiWrite("tick")(viewedScored.id, on)}
          onEdit={() => openEditor({ id: viewedScored.id })}
          // Offered only to whoever the server lets: the adder, or a trip admin.
          onRemove={
            canRemovePoi(viewedScored, me, isAdmin(trip.roles))
              ? () => poiRemove(viewedScored.id)
              : undefined
          }
        />
      )}
      {/* Adding a place: from the middle of the map, marked with a cross while the form is open. */}
      {!dock && addShown && (
        <button type="button" className="poi-add" onClick={() => openEditor("new")}>
          <Plus size={18} strokeWidth={2} aria-hidden="true" />
          Add place
        </button>
      )}
      {places && editing === "new" && <span className="poi-crosshair" aria-hidden="true" />}
      {places &&
        poiList.categories &&
        poiEditorOpen &&
        wrapEditor(
          <PoiEditor
            key={editing === "new" ? "new" : editedPoi!.id}
            poi={editedPoi}
            categories={poiList.categories}
            onSave={poiSave}
            onAddCategory={poiCategory}
            onVote={editedPoi ? (on) => poiWrite("vote")(editedPoi.id, on) : undefined}
            // Offered only to whoever the server lets: the adder, or a trip admin.
            onRemove={
              editedPoi && canRemovePoi(editedPoi, me, isAdmin(trip.roles))
                ? () => poiRemove(editedPoi.id)
                : undefined
            }
            onClose={() => setEditing(null)}
            where={
              mapCentre
                ? `Goes at the centre of the map, under the cross (${mapCentre[1].toFixed(4)}, ${mapCentre[0].toFixed(4)}). Pan the map to move it.`
                : "Goes at the centre of the map, under the cross. Pan the map to move it."
            }
          />,
        )}
      {!dock && trip.mapLayersEnabled && !searching && (
        <MapLayers
          layer={layer}
          onLayer={setLayer}
          className={`map-layers${phone && dialShown ? " above-dial" : ""}`}
        />
      )}
      {/* The phone's layer 2: one column along the bottom, the switch and the round + standing on
          the card; pulled up, the list is the whole map and they are under it. */}
      {dock && !searching && (
        <div className={`poi-dock${listFull ? " full" : ""}`}>
          {!listFull && (
            <div className="poi-dock-bar">
              <MapLayers layer={layer} onLayer={setLayer} className="map-layers" />
              {addShown && (
                <button
                  type="button"
                  className="poi-add round"
                  aria-label="Add place"
                  title="Add place"
                  onClick={() => openEditor("new")}
                >
                  <Plus size={24} strokeWidth={2} aria-hidden="true" />
                </button>
              )}
            </div>
          )}
          {!bare && poiSheet}
        </div>
      )}
      {tripLayer && !phone && dayTracks.length > 0 && (
        <ul className="legend plain" aria-label="Routes">
          {/* One row per member, from their own colour. The line itself is drawn in many
              shades of it — one per pace — so listing the drawn features would name the
              same person once for every time they changed speed. */}
          {dayTracks.map((t, i) => (
            <li key={t.userId}>
              <span
                className="swatch"
                style={{ background: routeColor(t, i) }}
                aria-hidden="true"
              />
              {t.displayName}
            </li>
          ))}
          <li>
            <span className="dot solid" aria-hidden="true" /> File GPS
          </li>
          <li>
            <span className="dot hollow" aria-hidden="true" /> Estimated
          </li>
        </ul>
      )}
    </div>
  );

  const side = (
    <div className="stack map-side">
      {current && (
        <Card title={current.kind === "VIDEO" ? "Video" : "Photo"}>
          <span className="small">
            {new Date(current.captureAt).toLocaleString()} · {name(current.contributorId)}
          </span>
          <NestedCard>
            <span className="small">{describeLocation(current)}</span>
          </NestedCard>
          {preview?.id === current.assetId ? (
            current.kind === "VIDEO" ? (
              <video src={preview.url} controls className="viewer" aria-label="Preview" />
            ) : (
              <img src={preview.url} alt="Preview" className="viewer" />
            )
          ) : (
            <div>
              <button className="button small" type="button" onClick={() => play(current)}>
                <Play {...ICON} aria-hidden="true" />
                Preview
              </button>
            </div>
          )}
          {previewError && (
            <p role="alert" className="error">
              {previewError}
            </p>
          )}
        </Card>
      )}
      <Card title={`In this window (${visible.length})`}>
        <ul className="plain clip-list">
          {visible.slice(0, 200).map((a) => (
            <li key={a.assetId}>
              <button
                type="button"
                className="clip-row"
                aria-pressed={selected === a.assetId}
                onClick={() => setSelected(a.assetId)}
              >
                {a.kind === "VIDEO" ? (
                  <Film {...ICON} aria-hidden="true" />
                ) : (
                  <ImageIcon {...ICON} aria-hidden="true" />
                )}
                <span className="grow">
                  {fmt(Date.parse(a.captureAt))} · {name(a.contributorId)}
                </span>
                {!a.location ? (
                  <Pill>No place</Pill>
                ) : a.location.source === "METADATA" ? (
                  <Pill tone="good">GPS</Pill>
                ) : (
                  <Pill tone="wait">Estimated</Pill>
                )}
              </button>
            </li>
          ))}
        </ul>
        {visible.length > 200 && (
          <p className="muted small">Showing the first 200. Narrow the window to see the rest.</p>
        )}
      </Card>
    </div>
  );

  const results = (
    <PageState
      data={assets}
      error={error}
      loading={loading}
      reload={reload}
      isEmpty={(d) => d.items.length === 0 && routes.features.length === 0}
      empty={
        <EmptyState
          icon={<MapPinOff size={24} strokeWidth={1.5} />}
          title="No footage on the map yet"
          body="Recordings land here once they are uploaded and dated. Their place comes from the file’s GPS, or from a traveller’s shared route."
          // An editor has no Sync section: the app would bounce them straight back here.
          {...(!editorOnly(trip.roles) && {
            action: {
              label: "Open Media Sync",
              onClick: () => navigate(tripPath(trip.id, "sync")),
            },
          })}
          // Only a trip with the layers on has a Places layer to go to.
          {...(trip.mapLayersEnabled && {
            secondary: { label: "See places instead", onClick: () => setLayer(2) },
          })}
        />
      }
    >
      {/* The map is not the footage's to withhold: it carries the other layers too, so it is
          drawn below these states rather than inside them, on a desktop as on a phone. */}
      {() => null}
    </PageState>
  );
  /** Footage to list beside the map: loaded, and something in it. */
  const hasFootage = assets !== null && (assets.items.length > 0 || routes.features.length > 0);

  if (phone) {
    return (
      <MapFullscreen
        title={trip.name}
        map={mapBox}
        days={
          daysShown ? (
            <MapDays
              days={days}
              current={currentDay}
              onPick={(t) => {
                setPlayhead(t);
                setFollow(false);
              }}
            />
          ) : null
        }
        action={
          // Searching takes it down too: it stands in the bar's row, and the map behind it is the
          // one strip the matches do not cover. Close stands where it was.
          !local?.here || bare || searching ? null : tripLayer ? (
            <button
              type="button"
              className={`map-chip icon${follow ? " on" : ""}`}
              aria-pressed={follow}
              aria-label={follow ? "Stop following your position" : "Centre on your position"}
              onClick={() => setFollow((f) => !f)}
            >
              <Crosshair size={18} strokeWidth={1.75} aria-hidden="true" />
            </button>
          ) : (
            <button
              type="button"
              className="map-chip icon"
              aria-label="Centre on your position"
              onClick={() => setRecentreAt([local.here!.lon, local.here!.lat])}
            >
              <Crosshair size={18} strokeWidth={1.75} aria-hidden="true" />
            </button>
          )
        }
        dial={
          dialShown ? (
            <TimeDial
              from={span.from}
              to={span.to}
              value={playhead}
              detents={detents}
              settleOn={settleOn}
              // Off for now, at the owner's call: the rim still marks every moment and the
              // arrows still step between them, but nothing tugs the thumb towards one.
              magnetic={false}
              boundaries={dayBoundaries}
              timeZone={TIME_ZONE}
              readout={readout}
              thumb={dialThumb}
              // The picture names one recording, so tapping it is the way to that recording: the
              // dial lands on its moment, and the map goes to where it was taken.
              onThumb={() => {
                if (atPlayhead === null) return;
                const t = Date.parse(atPlayhead.captureAt);
                if (Number.isFinite(t)) setPlayhead(Math.min(span.to, Math.max(span.from, t)));
                if (atPlayhead.location)
                  setGoTo([atPlayhead.location.lon, atPlayhead.location.lat]);
              }}
              turnMs={turnMs}
              onZoom={setTurnMs}
              onHold={(down) => {
                setHolding(down);
                // A fresh turn makes the last tidy-up stale: it must not fire into the middle
                // of the next one, nor re-run for a place the reader has already left.
                if (down) setSettleAt(null);
              }}
              onScrub={(t) => {
                setPlayhead(t);
                // Scrubbing is looking at the past; the map must stop chasing the live position,
                // exactly as panning away from it does — and go to where the past was instead.
                setFollow(false);
                const was = lookFor(t);
                if (was) setLookAt(was);
              }}
            />
          ) : null
        }
      >
        {/* Loading, failed, or nothing to place: all said about the footage, so layer 1's. */}
        {tripLayer && <div className="map-notice">{results}</div>}
        {tripLayer && openId && (
          <MapCardSheet
            trip={trip}
            assetId={openId}
            members={members.data?.items ?? []}
            onActive={(a) => {
              // The card being read sets the time underneath it: the pins, the travelled line
              // and the needle all move to the moment this picture was taken, so closing the
              // sheet leaves the map standing where the last thing read happened.
              const t = Date.parse(a.captureAt ?? "");
              if (!Number.isFinite(t)) return;
              setPlayhead(t);
              setFollow(false);
              // The recording's own place wins here: it is the thing being looked at.
              const where = a.location ? [a.location.lon, a.location.lat] : lookFor(t);
              if (where) setLookAt(where as [number, number]);
            }}
            onClose={() => setOpenId(null)}
          />
        )}
      </MapFullscreen>
    );
  }

  return (
    <div className="stack">
      {tripLayer && hasFootage && filters}
      {tripLayer && hasFootage && timeline}
      {tripLayer && results}
      <div className={`map-layout${tripLayer && hasFootage ? "" : " solo"}`}>
        {mapBox}
        {tripLayer && hasFootage && side}
      </div>
    </div>
  );
}
